import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import AuthModal from '@/components/AuthModal';
import { Home, User, History, LogOut, Menu, X, Moon, Sun } from 'lucide-react';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? saved === 'true' : false;
  });
  const location = useLocation();
  const navigate = useNavigate();

  // Apply dark mode to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="glass sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo and Dark Mode Toggle */}
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center">
                <img 
                  src="https://academiacortex.com.ar/imagenes/iconNegro.png" 
                  alt="Academia Cortex" 
                  className="h-10 w-auto dark:hidden" 
                />
                <img 
                  src="https://academiacortex.com.ar/imagenes/iconBlanco.png" 
                  alt="Academia Cortex" 
                  className="h-10 w-auto hidden dark:block" 
                />
              </Link>
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-full hover:bg-white/50 dark:hover:bg-gray-700/50 transition-colors"
                title={darkMode ? 'Modo claro' : 'Modo oscuro'}
              >
                {darkMode ? (
                  <Sun size={20} className="text-yellow-400" />
                ) : (
                  <Moon size={20} className="text-[#77C9D4]" />
                )}
              </button>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-6">
              <Link
                to="/"
                className={`flex items-center space-x-2 px-4 py-2 rounded-full hover:bg-white/50 ${
                  location.pathname === '/' ? 'bg-white/50 font-semibold' : ''
                }`}
              >
                <Home size={18} />
                <span>Inicio</span>
              </Link>
              {user && (
                <>
                  <Link
                    to="/history"
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full hover:bg-white/50 ${
                      location.pathname === '/history' ? 'bg-white/50 font-semibold' : ''
                    }`}
                  >
                    <History size={18} />
                    <span>Historial</span>
                  </Link>
                  {user.is_admin && (
                    <Link
                      to="/admin"
                      className={`flex items-center space-x-2 px-4 py-2 rounded-full hover:bg-white/50 ${
                        location.pathname === '/admin' ? 'bg-white/50 font-semibold' : ''
                      }`}
                    >
                      <User size={18} />
                      <span>Admin</span>
                    </Link>
                  )}
                </>
              )}
            </nav>

            {/* User section */}
            <div className="hidden md:flex items-center space-x-4">
              {user ? (
                <>
                  <div className="flex items-center space-x-2 px-4 py-2 bg-white/50 rounded-full">
                    <User size={18} className="text-[#77C9D4]" />
                    <span className="font-medium">{user.name}</span>
                  </div>
                  <Button
                    onClick={handleLogout}
                    variant="outline"
                    className="rounded-full"
                    data-testid="logout-button"
                  >
                    <LogOut size={18} />
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setShowAuthModal(true)}
                  className="rounded-full bg-[#77C9D4] hover:bg-[#5AB4C1] !text-white dark:!text-black"
                  data-testid="login-button"
                >
                  <User size={18} className="mr-2 dark:!text-black" />
                  Iniciar Sesión
                </Button>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <nav className="md:hidden mt-4 space-y-2">
              <Link
                to="/"
                className="flex items-center space-x-2 px-4 py-3 rounded-lg hover:bg-white/50"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Home size={18} />
                <span>Inicio</span>
              </Link>
              {user && (
                <Link
                  to="/history"
                  className="flex items-center space-x-2 px-4 py-3 rounded-lg hover:bg-white/50"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <History size={18} />
                  <span>Historial</span>
                </Link>
              )}
              {user ? (
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 px-4 py-3 rounded-lg hover:bg-white/50 w-full text-left"
                >
                  <LogOut size={18} />
                  <span>Cerrar Sesión</span>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setShowAuthModal(true);
                    setMobileMenuOpen(false);
                  }}
                  className="flex items-center space-x-2 px-4 py-3 rounded-lg hover:bg-white/50 w-full text-left"
                >
                  <User size={18} />
                  <span>Iniciar Sesión</span>
                </button>
              )}
            </nav>
          )}
        </div>
      </header>

      {/* Main content */}
      <main>{children}</main>

      {/* Auth Modal */}
      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
};

export default Layout;